import React from 'react';
import classNames from 'classnames';
import styled, { css } from 'styled-components';
import { ButtonProps } from '../button/button';
import { colors, spacing } from '../../style';
import Icon, { IconProps } from '../icon/icon';
import Text, { TextProps } from '../text/text';
import { borderRadiusOptions, variants } from './style';

export interface AlertProps {
  borderRadius?: keyof typeof borderRadiusOptions;
  borderRadiusTop?: keyof typeof borderRadiusOptions;
  variant?: keyof typeof variants;
  children?: React.ReactNode | string;
  className?: string;
  padding?: keyof typeof spacing;
  whiteSpace?: 'pre-line' | 'break-spaces' | 'nowrap' | 'pre-wrap';
  justifyContent?: 'center' | 'flex-start' | 'flex-end' | 'space-around' | 'space-between';

  alignItems?: 'center' | 'flex-start';
}

const AlertComponent = styled(({ children, className, variant = 'default' }: AlertProps) => (
  <div className={classNames('box-alert', className, `variant-${variant}`)}>{children}</div>
))`
  ${props => variants[props.variant || 'default']};
  position: relative;
  line-height: 1.25rem;
  border-radius: ${props => props.borderRadius && borderRadiusOptions[props.borderRadius]?.size};
  border-top-left-radius: ${props => props.borderRadiusTop && borderRadiusOptions[props.borderRadiusTop]?.size};
  border-top-right-radius: ${props => props.borderRadiusTop && borderRadiusOptions[props.borderRadiusTop]?.size};
  display: flex;
  align-items: ${props => props.alignItems || 'center'};
  gap: 16px;
  ${props =>
    props.padding &&
    css`
      padding: ${spacing[props.padding]};
    `};
  ${props =>
    props.justifyContent &&
    css`
      justify-content: ${props.justifyContent};
    `};
  ${props =>
    props.whiteSpace &&
    css`
      white-space: ${props.whiteSpace};
    `};

  .alert-icon {
    margin-left: ${spacing.xs};
  }
`;

const Alert = ({ ...props }: AlertProps) => <AlertComponent borderRadius={'xs'} padding={'xs'} {...props} />;

Alert.Text = ({ ...rest }: TextProps) => <Text weight={500} variant={'inherit'} {...rest} />;
Alert.Icon = ({ name, className, ...rest }: IconProps) => (
  <Icon className={classNames('alert-icon', className)} name={name} {...rest} />
);

interface AlertCloseButtonProps extends ButtonProps {
  onClose?: () => void;
  className?: string;
}

Alert.Close = styled(({ onClose, className }: AlertCloseButtonProps) => (
  <button className={className} onClick={onClose}>
    <Icon size={'md'} name={'CLOSE'} variant={'inherit'} />
  </button>
))`
  border: none;
  background: transparent;
  color: inherit;
  margin-left: auto;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  &:hover {
    cursor: pointer;
    i {
      transition: 0.2s;
      color: ${colors.negative};
    }
  }
`;

export default Alert;
